import React, { useRef } from "react";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

interface EmailDomainOption {
  value: string;
  label: string;
}

interface EmailVerificationProps {
  email: string;
  setEmail?: (value: string) => void;
  verificationMode?: "full" | "domain-select";
  emailLocalPart?: string;
  setEmailLocalPart?: (value: string) => void;
  selectedDomain?: string;
  setSelectedDomain?: (value: string) => void;
  domainOptions?: EmailDomainOption[];
  isDomainLocked?: boolean;
  onVerificationSent: () => void;
  isVerified: boolean;
  verificationSent: boolean;
  handleEditEmail: () => void | Promise<void>;
}

const inputClassName =
  "h-12 rounded-lg border-slate-200 bg-white px-4 text-[15px] text-slate-900 placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-[#fb5120] focus-visible:ring-offset-0";

const EmailVerification: React.FC<EmailVerificationProps> = ({
  email,
  setEmail,
  verificationMode = "full",
  emailLocalPart = "",
  setEmailLocalPart,
  selectedDomain = "",
  setSelectedDomain,
  domainOptions = [],
  isDomainLocked = false,
  onVerificationSent,
  isVerified,
  verificationSent,
  handleEditEmail,
}) => {
  const emailInputRef = useRef<HTMLInputElement>(null);
  const isDomainSelectMode = verificationMode === "domain-select";
  const selectedDomainOption =
    domainOptions.find((option) => option.value === selectedDomain) ?? null;
  const isFullEmailValid =
    /\S+@\S+\.\S+/.test(email) &&
    (!isDomainSelectMode || !emailLocalPart.includes("@"));
  const isSendButtonEnabled = isDomainSelectMode
    ? Boolean(emailLocalPart.trim() && selectedDomain) && isFullEmailValid
    : isFullEmailValid;

  return (
    <div className="space-y-5">
      {isDomainSelectMode ? (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label
                htmlFor="email-local-part"
                className="text-sm font-semibold text-slate-900"
              >
                메일 주소
              </Label>
              <Input
                id="email-local-part"
                className={inputClassName}
                placeholder="메일 앞부분을 입력해 주세요."
                value={emailLocalPart}
                onChange={(e) => setEmailLocalPart?.(e.target.value)}
                ref={emailInputRef}
                disabled={verificationSent || isVerified}
                autoComplete="username"
              />
              {emailLocalPart.includes("@") &&
                !verificationSent &&
                !isVerified && (
                  <p className="text-sm text-rose-600">
                    메일 앞부분만 입력해 주세요. 도메인은
                    {isDomainLocked
                      ? " 비밀코드에 따라 자동 지정됩니다."
                      : " 오른쪽에서 선택합니다."}
                  </p>
                )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-900">
                도메인
              </Label>
              {isDomainLocked ? (
                <>
                  <Input
                    value={selectedDomainOption?.label ?? ""}
                    className={inputClassName}
                    disabled
                    readOnly
                  />
                  <p className="text-sm text-slate-500">
                    비밀코드에 연결된 고객사 도메인이 자동 지정되었습니다.
                  </p>
                </>
              ) : (
                <Select
                  value={selectedDomain}
                  onValueChange={(value) => setSelectedDomain?.(value)}
                  disabled={verificationSent || isVerified}
                >
                  <SelectTrigger className={inputClassName}>
                    <SelectValue placeholder="도메인을 선택해 주세요." />
                  </SelectTrigger>
                  <SelectContent>
                    {domainOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-xs font-semibold tracking-[0.14em] text-slate-500">
              인증 메일 주소
            </div>
            <div className="mt-2 break-all text-sm font-medium text-slate-900">
              {isSendButtonEnabled
                ? email
                : isDomainLocked
                  ? "메일 주소 앞부분을 입력하면 인증 주소가 표시됩니다."
                  : "메일 주소와 도메인을 선택하면 인증 주소가 표시됩니다."}
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-2">
          <Label
            htmlFor="full-email"
            className="text-sm font-semibold text-slate-900"
          >
            이메일 주소
          </Label>
          <Input
            id="full-email"
            className={inputClassName}
            placeholder="이메일 주소를 입력해 주세요."
            value={email}
            onChange={(e) => setEmail?.(e.target.value)}
            ref={emailInputRef}
            disabled={verificationSent || isVerified}
            autoComplete="email"
          />
        </div>
      )}

      {!verificationSent && !isVerified && (
        <Button
          type="button"
          onClick={onVerificationSent}
          className="h-12 w-full rounded-lg bg-[#fb5120] font-semibold text-white hover:bg-[#e5481b] disabled:bg-slate-200 disabled:text-slate-500"
          disabled={!isSendButtonEnabled}
        >
          인증 메일 전송
        </Button>
      )}

      {verificationSent && !isVerified && (
        <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm leading-6 text-amber-900">
            이메일을 확인하고 인증을 완료해 주세요.
          </p>
          <Button
            type="button"
            variant="outline"
            className="h-11 rounded-lg border-amber-300 bg-white px-4 text-amber-900 hover:bg-amber-100 hover:text-amber-900"
            onClick={handleEditEmail}
          >
            메일 주소 수정
          </Button>
        </div>
      )}

      {isVerified && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-800">
          이메일 인증이 완료되었습니다. 아래에서 가입 정보를 입력해 주세요.
        </div>
      )}
    </div>
  );
};

export default EmailVerification;
